import { t } from 'i18next';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import { FgDialog } from '@/components';
import {
  baseStyles,
  buttonStyles,
  formStyles,
  typographyStyles,
} from '@/styles';
import { TokenEnvironments } from '@/types';

@customElement('fg-create-token-dialog')
export class FgCreateTokenDialog extends LitElement {
  @state()
  private newToken = { environment: TokenEnvironments.PRODUCTION, name: '' };

  @state()
  private invalidName = false;

  private tokenNameInput: Ref<HTMLInputElement> = createRef();

  private fgDialogRef: Ref<FgDialog> = createRef();

  openDialog() {
    this.fgDialogRef.value?.open();
  }

  closeDialog() {
    this.fgDialogRef.value?.close();
  }

  private createToken() {
    if (!this.newToken.name) {
      this.invalidName = true;
      this.tokenNameInput.value?.focus();
      return;
    }

    this.dispatchEvent(
      new CustomEvent('create-token', {
        detail: {
          token: {
            environment: this.newToken.environment,
            name: this.newToken.name,
          },
        },
      }),
    );
    this.newToken = { environment: TokenEnvironments.PRODUCTION, name: '' };
  }

  private updateToken(
    event: { target: HTMLSelectElement },
    keyName: 'name' | 'environment',
  ) {
    this.newToken = {
      ...this.newToken,
      [keyName]: event?.target?.value,
    };
  }

  render() {
    return html`
      <fg-dialog part="wholesale" ${ref(this.fgDialogRef)} compact closable>
        <span slot="title">${t('actions.createToken')}</span>
        <div class="create-token-container">
          <div class="create-token-input-container">
            <label for="token-name">
              ${t('content.debtorToken.tokenName')}
            </label>
            <input
              ${ref(this.tokenNameInput)}
              id="token-name"
              type="text"
              aria-invalid="${this.invalidName}"
              class="${classMap({ warning: this.invalidName })}"
              .value="${this.newToken.name}"
              @change="${(event: { target: HTMLSelectElement }) =>
                this.updateToken(event, 'name')}"
            />
          </div>
          <div class="create-token-input-container">
            <label for="environment">
              ${t('content.debtorToken.environment')}
            </label>
            <select
              id="environment"
              value="${this.newToken.environment}"
              @change="${(event: { target: HTMLSelectElement }) =>
                this.updateToken(event, 'environment')}"
            >
              <option value="${TokenEnvironments.PRODUCTION}">
                ${TokenEnvironments.PRODUCTION}
              </option>
              <option value="${TokenEnvironments.SANDBOX}">
                ${TokenEnvironments.SANDBOX}
              </option>
            </select>
          </div>
        </div>
        <div slot="footer">
          <div class="actions">
            <button
              class="button button-tertiary"
              @click="${() => this.fgDialogRef.value?.close()}"
            >
              ${t('actions.cancel')}
            </button>
            <button class="button button-primary" @click="${this.createToken}">
              ${t('actions.create')}
            </button>
          </div>
        </div>
      </fg-dialog>
      <fg-message-snack-bar
        part="wholesale"
        ?show="${Boolean(this.invalidName)}"
        .type="${'error'}"
        autoDisappear
        message="${t('errors.noName') || ''}"
        @on-close="${() => (this.invalidName = false)}"
      ></fg-message-snack-bar>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    buttonStyles,
    formStyles,
    css`
      .actions {
        width: 100%;
        gap: 16px;
        display: flex;
      }

      .button {
        width: 100%;
      }

      .actions label {
        font-size: 1rem;
        font-weight: 700;
      }

      .create-token-input-container {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .create-token-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 20px;
      }
    `,
  ];
}
